// Invite & manage users — onboarding new accounts and the admin actions on
// existing ones. Sourced from specs/cli.md §3–5 and specs/web-app.md (Users).

import { IconMailForward, IconTerminal2, IconUserCog } from "@tabler/icons-react";
import { CommandLine } from "@/components/command-line";
import { DocSection, K, P, Points } from "../doc-ui";

export function InviteUsersDoc() {
  return (
    <>
      <P>
        New people join by <strong>account invite</strong>: a manager or admin mints an invite
        link, the person opens it, sets a password, and lands signed in. The <K>Users</K> page
        (sidebar menu, visible to members and up) is the roster; invite and admin actions appear
        there according to your role.
      </P>

      <DocSection
        icon={IconMailForward}
        title="Invite someone"
        description="Requires the manager or admin server role."
      >
        <Points>
          <li>
            On the Users page, create an invite (optionally recording their email) and copy the
            link — send it over whatever channel you trust.
          </li>
          <li>Pending invites are listed until accepted; you can cancel one, or regenerate its
            link (the old link stops working).</li>
          <li>
            New accounts start with the <K>member</K> role. See <em>Roles &amp; permissions</em>{" "}
            for what that means and how to change it.
          </li>
        </Points>
        <CommandLine command="cairn user invite --email person@example.com" />
        <CommandLine command="cairn user invites" comment="list pending invites" />
      </DocSection>

      <DocSection
        icon={IconUserCog}
        title="Manage existing users"
        description="Admin-only actions, from the row menu on the Users page or the CLI."
      >
        <Points>
          <li>
            <strong>Set server role</strong> — admin / manager / member / guest.
          </li>
          <li>
            <strong>Disable / enable</strong> — a disabled account can't sign in or sync, but
            nothing is deleted.
          </li>
          <li>
            <strong>Password reset link</strong> — mint a link for someone who's locked out.
          </li>
          <li>
            <strong>Clear two-factor</strong> — for a user who lost their authenticator; they can
            sign in with just their password and re-enroll.
          </li>
          <li>
            <strong>Delete</strong> — permanent. A user who still owns vaults can't be deleted;
            transfer or delete those vaults first.
          </li>
        </Points>
        <CommandLine command="cairn user list" comment="the same roster, in the terminal" />
        <CommandLine command="cairn user set-role <user> <role>" />
        <CommandLine command="cairn user disable <user>" />
        <CommandLine command="cairn user reset-password <user>" comment="prints the link" />
      </DocSection>

      <DocSection icon={IconTerminal2} title="Safety rails">
        <Points>
          <li>
            <strong>Last admin</strong> — the last admin can't be demoted, disabled, or deleted.
            If you ever lock yourself out anyway, the server host can recover with the{" "}
            <K>cairn-server admin</K> bootstrap tool.
          </li>
          <li>
            <strong>Guest ceiling</strong> — demoting someone to <K>guest</K> is refused while
            they hold vault roles above viewer; downgrade those memberships first.
          </li>
        </Points>
      </DocSection>
    </>
  );
}
